import { Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, ResolveEnd, Router } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { filter } from 'rxjs/operators';
import { datadogRum } from '@datadog/browser-rum';
import { UserProfileService } from './user-profile.service';
import { APP_CONFIG, AppConfiguration } from '../types/app-configuration';
import { UserProfile } from './application-api.service';

@Injectable({ providedIn: 'root' })
export class ApplicationInsightsService {

  private appInsights: ApplicationInsights | undefined;

  constructor(@Inject(APP_CONFIG) public appConfig: AppConfiguration, private router: Router, profile: UserProfileService) {
    this.appInsights = undefined;
    if (!appConfig.production) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: this.appConfig.applicationInsightsConnString,
          enableCorsCorrelation: !this.appConfig.production,
          enableAutoRouteTracking: !this.appConfig.production
        },
      });

      this.appInsights.loadAppInsights();

      profile.user$.pipe(takeUntilDestroyed(), filter(u => !!u)).subscribe(u => this.setUserId(u!));

      this.router.events.pipe(
        takeUntilDestroyed(),
        filter((event): event is ResolveEnd => event instanceof ResolveEnd)
      ).subscribe(event => {
        const activatedComponent = this.getActivatedComponent(event.state.root);
        if (activatedComponent) {
          this.logPageView(
            `${activatedComponent.name} ${this.getRouteTemplate(event.state.root)}`,
            event.urlAfterRedirects
          );
        }
      });
    }

  }

  logException(exception: Error, severityLevel?: number) {
    if (this.appInsights)
      this.appInsights.trackException({ exception: exception, severityLevel: severityLevel });
  }

  private setUserId(user: UserProfile) {
    if (user.emailAddress) {
      if (this.appConfig.production) {
        datadogRum.setUser({
           id: user.userName!,
           name: user.lastName + ', ' + user.firstName,
           email: user.emailAddress
        });
      }
      else {
        if (this.appInsights)
          this.appInsights.setAuthenticatedUserContext(user.emailAddress, user.lastName + '/' + user.firstName, true);
      }
    }
  }

  private logPageView(name?: string, uri?: string) {
    if (this.appInsights)
      this.appInsights.trackPageView({ name, uri });
  }

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';
    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

}
